// Minimal DOM helpers. Deliberately not a framework: the reader has to do
// imperative DOM surgery on arbitrary article HTML (highlights can't be
// rendered through a virtual DOM), so a framework would earn its keep only in
// the list views — not enough to justify a build step for either consumer.

import { isUploadUrl } from './upload.ts';

/** Escape text for interpolation into an HTML string. */
export function esc(value: unknown): string {
  if (value == null) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Create an element: el('div', {class: 'x'}, 'text' | node | [children]) */
type Attrs = Record<string, unknown> & { dataset?: Record<string, string> };
type Child = Node | string | number | null | undefined | false;

export function el(tag: string, attrs: Attrs = {}, children: Child | Child[] = []): HTMLElement {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (value == null || value === false) continue;
    if (key === 'class') node.className = String(value);
    else if (key === 'dataset') {
      for (const [dataKey, dataValue] of Object.entries(value as Record<string, string>)) {
        node.dataset[dataKey] = dataValue;
      }
    }
    else if (key.startsWith('on') && typeof value === 'function') {
      node.addEventListener(key.slice(2).toLowerCase(), value as EventListener);
    } else if (value === true) node.setAttribute(key, '');
    else node.setAttribute(key, String(value));
  }
  for (const child of ([] as Child[]).concat(children)) {
    if (child == null || child === false) continue;
    node.append(child instanceof Node ? child : document.createTextNode(String(child)));
  }
  return node;
}

export function hostname(url: string): string {
  if (isUploadUrl(url)) return 'uploaded file';
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/** "3 min read" from a word count, at a middling adult reading speed. */
export function readingTime(wordCount: number | null | undefined): string | null {
  if (!wordCount) return null;
  return `${Math.max(1, Math.round(wordCount / 220))} min read`;
}

/** "45 min" or "7 h 32 min", for something to listen to. */
export function listeningTime(seconds: number | null | undefined): string | null {
  if (!seconds) return null;
  const minutes = Math.max(1, Math.round(seconds / 60));
  return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '';
  // SQLite's CURRENT_TIMESTAMP is "YYYY-MM-DD HH:MM:SS" in UTC with no zone
  // marker, which Date parses as local time — an hours-off timestamp. Make the
  // zone explicit before parsing.
  const iso = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)
    ? `${value.replace(' ', 'T')}Z`
    : value;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Trailing-edge debounce; used for scroll-progress writes. */
export interface Debounced<A extends unknown[]> {
  (...args: A): void;
  cancel(): void;
  flush(...args: A): void;
}

export function debounce<A extends unknown[]>(fn: (...args: A) => unknown, ms: number): Debounced<A> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const wrapped = ((...args: A) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  }) as Debounced<A>;
  wrapped.cancel = () => clearTimeout(timer);
  wrapped.flush = (...args: A) => {
    clearTimeout(timer);
    fn(...args);
  };
  return wrapped;
}
